"use client";
import { useSession } from "../../lib/session";
import { useState } from "react";
import { Button, Fact, Facts, StatusTag } from ".";
import { formatTime } from "../../lib/api";
import { ClueAttachments, ParticipationLink, ParticipationTimeline } from "./participation-tools";
import {
  CALL_LABEL,
  CLUE_LABEL,
  CLUE_TONE,
  CLUE_KIND_LABEL,
  DISPOSITION_LABEL,
  NEXT_STATUS,
  OUTCOME_LABEL,
  SUBMISSION_LABEL,
  TOPIC_LABEL,
  type CallDetail,
  type ClueDetail,
  type Editor,
  type TopicDetail
} from "./participation-model";
export function ClueReading({
  record,
  onAction
}: {
  record: ClueDetail;
  onAction: (editor: Editor) => void;
}) {
  const [tab, setTab] = useState("content");
  return (
    <>
      <DetailTabs
        value={tab}
        onChange={setTab}
        items={[
          ["content", "正文与附件"],
          ["history", "回执与记录"]
        ]}
      />
      <div className="record-sheet-body participation-reading">
        {tab === "content" ? (
          <>
            <section className="participation-paper">
              <div className="participation-byline">
                <span>
                  {CLUE_KIND_LABEL[record.kind ?? "ISSUE_CLUE"]} · {record.member.displayName} · {record.organization.name}
                </span>
                <time>{formatTime(record.submittedAt)} 提交</time>
              </div>
              <h2>{record.title}</h2>
              {/* 2026-10-24 起会员端只填标题与内容（内容落在 situation）；旧稿仍按两段显示。 */}
              <h3>{record.content.suggestion ? "情况说明" : "内容"}</h3>
              <p>{record.content.situation}</p>
              {record.content.suggestion ? (
                <>
                  <h3>意见建议</h3>
                  <p>{record.content.suggestion}</p>
                </>
              ) : null}
              <Facts>
                <Fact label="涉及地区">{record.content.regionSummary || "未填写"}</Fact>
                <Fact label="线索来源">
                  {(
                    {
                      PERSONAL_OBSERVATION: "亲身观察",
                      FIELD_RESEARCH: "调研走访",
                      WORK_PRACTICE: "工作实践",
                      OTHER: "其他来源"
                    } as Record<string, string>
                  )[record.content.sourceType] ?? record.content.sourceType}
                </Fact>
                <Fact label="联系偏好">
                  {(
                    { IN_APP: "站内联系", PHONE: "可电话联系", NONE: "暂不联系" } as Record<
                      string,
                      string
                    >
                  )[record.content.contactPreference] ?? record.content.contactPreference}
                </Fact>
              </Facts>
            </section>
            <ClueAttachments key={record.id} id={record.id} />
            {record.task && (
              <section className="record-section">
                <h3>所属征集</h3>
                <ParticipationLink href={`/calls?record=${encodeURIComponent(record.task.id)}`}>
                  {record.task.title}
                </ParticipationLink>
              </section>
            )}
            {record.status === "ACCEPTED_FOR_FOLLOW_UP" && (
              <section className="record-section">
                <h3>后续研究</h3>
                <p className="record-muted">可以据此提出课题，或在已有课题中关联本条建言。</p>
                <ParticipationLink href={`/topics?newFrom=${encodeURIComponent(record.id)}`}>
                  从这条建言提出课题
                </ParticipationLink>
              </section>
            )}
          </>
        ) : (
          <>
            <section className="participation-receipt-preview">
              <span>会员可见的当前回执</span>
              <h3>{record.receipt.label}</h3>
              <p>{record.receipt.meaning}</p>
              {record.mergedIntoPublicRef && <p>归并去向：{record.mergedIntoPublicRef}</p>}
            </section>
            <section className="record-section">
              <h3>处理记录</h3>
              <p className="record-muted">按办理时点呈现状态与会员可见说明，正文版本分别保留。</p>
              <ParticipationTimeline
                events={record.timeline}
                label={{ ...CLUE_LABEL, DRAFT: "本人草稿" }}
              />
            </section>
          </>
        )}
      </div>
      <div className="participation-detail-footer">
        <span>
          {record.status === "SUPPLEMENT_REQUIRED"
            ? "等待会员补充后重新提交"
            : record.status === "ACCEPTED_FOR_FOLLOW_UP"
              ? "已进入后续研判"
              : record.status === "IN_REVIEW"
                ? "核对材料后作出办理决定"
                : record.status === "SUBMITTED"
                  ? "这条建言等待受理"
                  : "查看已留存的办理记录"}
        </span>
        <div className="workspace-actions">
          {record.status === "SUBMITTED" && (
            <Button onClick={() => onAction({ kind: "clue", record, action: "start-review" })}>
              开始受理
            </Button>
          )}
          {record.status === "IN_REVIEW" && (
            <>
              <Button
                variant="ghost"
                onClick={() => onAction({ kind: "clue", record, action: "merge" })}
              >
                归并
              </Button>
              <Button
                variant="ghost"
                onClick={() => onAction({ kind: "clue", record, action: "not-accept" })}
              >
                本轮不跟进
              </Button>
              <Button
                variant="outline"
                onClick={() => onAction({ kind: "clue", record, action: "request-supplement" })}
              >
                请会员补充
              </Button>
              <Button
                onClick={() => onAction({ kind: "clue", record, action: "accept-for-follow-up" })}
              >
                进入后续跟进
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
export function CallReading({
  record,
  onAction
}: {
  record: CallDetail;
  onAction: (editor: Editor) => void;
}) {
  const [tab, setTab] = useState("content");
  return (
    <>
      <DetailTabs
        value={tab}
        onChange={setTab}
        items={[
          ["content", "征集说明"],
          ["responses", `已交材料 · ${record.responseCount}`],
          ["history", "办理记录"]
        ]}
      />
      <div className="record-sheet-body participation-reading">
        {tab === "content" && (
          <>
            <section className="participation-paper">
              <p className="record-eyebrow">{record.category}</p>
              <h2>{record.title}</h2>
              <p>{record.description}</p>
              {record.requirements.length > 0 && (
                <>
                  <h3>材料要求</h3>
                  <ol>
                    {record.requirements.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ol>
                </>
              )}
              <Facts>
                <Fact label="承办组织">{record.organization.name}</Fact>
                <Fact label="收件范围">发起时确定的 {record.audienceCount} 名会员</Fact>
                <Fact label="开始时间">{formatTime(record.opensAt)}</Fact>
                <Fact label="截止时间">
                  {record.closesAt ? formatTime(record.closesAt) : "长期征集"}
                </Fact>
                <Fact label="报送目标">{record.targetLabel || "未填写"}</Fact>
              </Facts>
              {record.withdrawnReason && (
                <div className="participation-receipt-preview">
                  <strong>撤回理由</strong>
                  <p>{record.withdrawnReason}</p>
                </div>
              )}
            </section>
          </>
        )}
        {tab === "responses" && (
          <section className="record-section">
            <div className="record-section-title">
              <h3>已交材料</h3>
              <ParticipationLink href={`/clues?task=${encodeURIComponent(record.id)}`}>
                查看全部材料
              </ParticipationLink>
            </div>
            <p className="record-muted">
              已交 {record.responseCount} 份，其中 {record.acceptedCount}{" "}
              份进入后续跟进。以下为本次读取的最近 {record.responses.length} 份摘要。
            </p>
            {record.responses.length === 0 ? (
              <p className="record-muted">尚未收到已提交的材料。</p>
            ) : (
              <ul className="participation-linked-list">
                {record.responses.map((r) => (
                  <li key={r.submissionId}>
                    <div>
                      <ParticipationLink
                        href={`/clues?record=${encodeURIComponent(r.submissionId)}`}
                      >
                        {r.title}
                      </ParticipationLink>
                      <p>
                        {r.publicRef} · {r.memberDisplayName}
                        {r.submittedAt ? ` · ${formatTime(r.submittedAt)}` : ""}
                      </p>
                    </div>
                    <StatusTag tone={CLUE_TONE[r.status as keyof typeof CLUE_TONE] ?? "neutral"}>
                      {CLUE_LABEL[r.status as keyof typeof CLUE_LABEL] ?? r.status}
                    </StatusTag>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        {tab === "history" && (
          <section className="record-section">
            <h3>办理记录</h3>
            <ParticipationTimeline events={record.timeline} label={CALL_LABEL} />
          </section>
        )}
      </div>
      <div className="participation-detail-footer">
        <span>
          {record.status === "PUBLISHED"
            ? "已交材料持续保留，逐份进入建言受理"
            : "已交材料和办理记录继续保留"}
        </span>
        {record.status === "PUBLISHED" && (
          <div className="workspace-actions">
            <Button
              variant="ghost"
              onClick={() => onAction({ kind: "call", action: "withdraw", record })}
            >
              撤回征集
            </Button>
            <Button
              variant="outline"
              onClick={() => onAction({ kind: "call", action: "close", record })}
            >
              截止收件
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
export function TopicReading({
  record,
  onAction
}: {
  record: TopicDetail;
  onAction: (editor: Editor) => void;
}) {
  const [tab, setTab] = useState("overview");
  const { hasRole } = useSession();
  return (
    <>
      <DetailTabs
        value={tab}
        onChange={setTab}
        items={[
          ["overview", "概况与来源"],
          ["manuscripts", `稿件 · ${record.manuscriptCount}`],
          ["submissions", "报送与回执"],
          ["history", "办理记录"]
        ]}
      />
      <div className="record-sheet-body participation-reading">
        {tab === "overview" && (
          <>
            <section className="record-section">
              <h2>{record.title}</h2>
              <Facts>
                <Fact label="年度 / 方向">
                  {record.year} 年 · {record.direction}
                </Fact>
                <Fact label="牵头负责人">{record.leadLabel}</Fact>
                <Fact label="参与专家">{record.expertLabels.join("、") || "未登记"}</Fact>
                <Fact label="承办组织">{record.organization.name}</Fact>
                {record.disposition && (
                  <Fact label="后续处置">{DISPOSITION_LABEL[record.disposition]}</Fact>
                )}
              </Facts>
            </section>
            <section className="record-section">
              <div className="record-section-title">
                <h3>来源建言 · {record.clueLinks.length}</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAction({ kind: "topic", action: "link", record })}
                >
                  关联来源
                </Button>
              </div>
              {record.clueLinks.length === 0 && (
                <p className="record-muted">尚未关联建言。可选择已进入后续跟进的材料。</p>
              )}
              <ul className="participation-linked-list">
                {record.clueLinks.map((c) => (
                  <li key={c.clueSubmissionId}>
                    <div>
                      <ParticipationLink
                        href={`/clues?record=${encodeURIComponent(c.clueSubmissionId)}`}
                      >
                        {c.title}
                      </ParticipationLink>
                      <p>
                        {c.publicRef} · {c.memberDisplayName} · {formatTime(c.linkedAt)} 关联
                      </p>
                      {c.note && <p>{c.note}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
            <section className="record-section">
              <div className="record-section-title">
                <h3>调研记录 · {record.fieldTrips.length}</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAction({ kind: "topic", action: "trip", record })}
                >
                  登记调研
                </Button>
              </div>
              {record.fieldTrips.length === 0 && <p className="record-muted">尚未登记调研记录。</p>}
              {record.fieldTrips.map((r, i) => (
                <article className="record-timeline-item" key={i}>
                  <time>{formatTime(r.occurredAt)}</time>
                  <h3>{r.place}</h3>
                  <p className="participation-prose">{r.note}</p>
                </article>
              ))}
            </section>
          </>
        )}
        {tab === "manuscripts" && (
          <section className="record-section">
            <div className="record-section-title">
              <h3>稿件版本</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAction({ kind: "topic", action: "manuscript", record })}
              >
                新增稿件版本
              </Button>
            </div>
            <p className="record-muted">每次保存形成新版本，报送记录始终指向当时选定的版本。</p>
            {record.manuscripts.length === 0 && <p className="record-muted">尚未登记稿件版本。</p>}
            {[...record.manuscripts]
              .sort((a, b) => b.versionNumber - a.versionNumber)
              .map((m) => (
                <article className="participation-manuscript" key={m.versionNumber}>
                  <header>
                    <span className="participation-version">v{m.versionNumber}</span>
                    <div>
                      <h3>{m.title}</h3>
                      <p>
                        {m.docType} · {m.authorLabel} · {formatTime(m.recordedAt)}
                        {m.aiAssisted ? " · AI 辅助整理" : ""}
                      </p>
                    </div>
                  </header>
                  <p className="participation-prose">{m.summary}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      onAction({
                        kind: "topic",
                        action: "submission",
                        record,
                        versionNumber: m.versionNumber
                      })
                    }
                  >
                    基于 v{m.versionNumber} 生成报送记录
                  </Button>
                </article>
              ))}
          </section>
        )}
        {tab === "submissions" && (
          <section className="record-section">
            <h3>报送与外部反馈</h3>
            <p className="record-muted">
              实际报送与外部反馈由负责人员在移动工作台登记。每条记录分别保留报送版本、目标和反馈依据。
            </p>
            {record.submissions.length === 0 && (
              <p className="record-muted">尚无报送记录，可从稿件版本生成待报送记录。</p>
            )}
            {record.submissions.map((s) => (
              <article className="participation-submission" key={s.id}>
                <div className="record-section-title">
                  <h3>{s.targetLabel}</h3>
                  <StatusTag tone={s.status === "PREPARED" ? "neutral" : "brand"}>
                    {SUBMISSION_LABEL[s.status]}
                  </StatusTag>
                </div>
                <p>
                  {s.publicRef} · {s.docType} · 稿件 v{s.manuscriptVersionNumber}
                </p>
                <p className="record-muted">
                  {s.submittedAt ? `${formatTime(s.submittedAt)} 报送` : "尚未登记实际报送时间"}
                </p>
                {hasRole("PARTICIPATION_REVIEWER") && s.status === "SUBMITTED" && <Button variant="outline" size="sm" onClick={() => onAction({ kind: "topic", action: "no-response", record, submission: s })}>登记未获反馈</Button>}
                {s.status === "NO_RESPONSE" && <p className="record-muted">本次登记未获反馈，结果未知；不代表未采用或未通过。</p>}
                {s.outcomes.length === 0 ? (
                  <p className="record-muted">尚未登记外部反馈。</p>
                ) : (
                  s.outcomes.map((o, i) => (
                    <div className="participation-outcome" key={i}>
                      <strong>{OUTCOME_LABEL[o.kind] ?? o.kind}</strong>
                      <p>{o.note}</p>
                      <p>依据：{o.evidenceRef || "未附证据编号"}</p>
                      <small>
                        发生时间：{o.occurredAt ? formatTime(o.occurredAt) : "未登记"} · 登记于{" "}
                        {formatTime(o.recordedAt)}
                      </small>
                    </div>
                  ))
                )}
              </article>
            ))}
          </section>
        )}
        {tab === "history" && (
          <section className="record-section">
            <h3>课题办理记录</h3>
            <ParticipationTimeline events={record.timeline} label={TOPIC_LABEL} />
          </section>
        )}
      </div>
      <div className="participation-detail-footer">
        <span>当前：{TOPIC_LABEL[record.status]}</span>
        <div className="workspace-actions">
          {NEXT_STATUS[record.status].map((status) => (
            <Button
              key={status}
              variant={status === "CLOSED" ? "ghost" : "outline"}
              size="sm"
              onClick={() =>
                onAction({ kind: "topic", action: "status", record, nextStatus: status })
              }
            >
              {status === "APPROVED"
                ? "确认立项"
                : status === "ACTIVE"
                  ? "进入调研"
                  : status === "COMPLETED"
                    ? "登记完成"
                    : status === "ON_HOLD"
                      ? "暂缓课题"
                      : "结项处置"}
            </Button>
          ))}
        </div>
      </div>
    </>
  );
}
export function DetailTabs({
  value,
  onChange,
  items
}: {
  value: string;
  onChange: (value: string) => void;
  items: string[][];
}) {
  return (
    <nav className="record-tabs" aria-label="详情内容">
      {items.map(([id, label]) => (
        <button
          key={id}
          aria-current={id === value ? "page" : undefined}
          onClick={() => onChange(id)}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}
